import React, { useMemo } from 'react';
import { differenceInCalendarDays, isBefore, isAfter, eachDayOfInterval, addDays } from 'date-fns';
import makeStyles from '@mui/styles/makeStyles';
import { filter, size, set, get, reduce, cloneDeep, head, last, some, uniq, values } from 'lodash';
import clsx from 'clsx';
import { useHistory } from 'react-router-dom';
import { noop } from 'common';
import { useSnackbar } from 'notistack';
import NiceModal from '@ebay/nice-modal-react';

import { useApi } from 'common/hooks/api';
import { useSegments, useSegmentsOriginal, useItineraryOriginal } from './hooks';
import { useQuoteOnly } from './hooks';
import { useGetQuoteMutation, useCalendarBySupplierWithRoomsMulti } from './queries';
import { useItineraryUpdateAndRequote } from 'api';
import { useBuilderStore } from './store';
import { STORE_LOCAL_STORAGE_KEY } from './constants';
import { useUser } from 'common/hooks/user';
import { useRates } from 'hooks/rates';

import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import ErrorIcon from '@mui/icons-material/Error';

import {
  ITINERARY_FLAG,
  CALENDAR_STATE,
  ITINERARY_ORIGIN,
  OWN_ACCOMMODATION_SUPPLIER_CODE,
  paxToDefaultRooms,
  itineraryStateConfirmed,
  setItineraryFlag,
  segmentsDateFloat,
} from 'itrvl-types';
import { Button, CircularProgress } from '@mui/material';
import { formatDate, parseDate } from './utils';
import Tooltip from 'components/v2/Tooltip';
import ConfirmDenyModal from 'components/v2/Modals/ConfirmDenyModal';

import { Grid } from '@mui/material';
import sharedStyles from 'styles/shared';
import CheckboxGroup from 'components/v2/FormElements/CheckboxGroupRhf';
import { useForm } from 'react-hook-form';
import { retainDetailsPreQuote } from './copyToBuilder';

import logger from 'itrvl-logger';
const log = logger(__filename);
log.trace(__filename);

const useStyles = makeStyles(theme => ({
  '@global': {
    '& .baseModalSubtext': {
      display: 'block',
    },
    '& .formGroup': {
      flexDirection: 'column',
    },
  },
  btn: {
    display: 'flex',
    gap: 8,
    textTransform: 'uppercase',
    height: '100%',
    fontWeight: 400,
    lineHeight: 1,
    fontSize: 12,
  },
  wrapper: {
    height: '100%',
  },
  disabled: {
    cursor: 'not-allowed',
  },
  loader: {
    color: '#fff',
  },
  error: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    '& svg': {
      color: theme.palette.secondary.main,
      width: 16,
    },
  },
}));

const GetQuoteButton = () => {
  const s = sharedStyles();
  const classes = useStyles();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const user = useUser();
  const Api = useApi();
  const client = user?.client;
  const rates = useRates();
  const segments = useSegments();
  const segmentsOriginal = useSegmentsOriginal();
  const itineraryOriginal = useItineraryOriginal();
  const hasSegments = size(segments) > 0;
  const adults = useBuilderStore(state => state.data.adults);
  const children = useBuilderStore(state => state.data.children);
  const childrenAges = cloneDeep(useBuilderStore(state => state.data.childrenAges));
  const minTripChildAge = children > 0 && childrenAges ? Math.min(...childrenAges) : undefined;
  const defaultRooms = paxToDefaultRooms(adults, childrenAges);
  const copiedFrom = useBuilderStore(state => state.data.copiedFrom);
  const honeymoon = useBuilderStore(state => state.data.honeymoon);
  const replaceBooking = useBuilderStore(state => state.data.replaceBooking);
  const reset = useBuilderStore(state => state.actions.reset);

  const getQuoteMutation = useGetQuoteMutation();
  const itineraryUpdateAndRequote = useItineraryUpdateAndRequote({
    onSuccess: _data => {
      setTimeout(() => {
        history.push(`/itinerary/${itineraryOriginal?.id}`);
      }, 1);
      localStorage.removeItem(STORE_LOCAL_STORAGE_KEY);
      reset();
    },
    onError: () => {
      enqueueSnackbar('Unable to replace booking', { variant: 'error', anchorOrigin: { horizontal: 'right', vertical: 'bottom' } });
    },
  });
  const loading = getQuoteMutation.isLoading || itineraryUpdateAndRequote.isLoading;

  const { control, setValue, getValues } = useForm({
    defaultValues: { retainActivities: false, retainTransit: false, retainDmc: false, retainPresentationEdits: false },
  });

  const retainOptions = [
    { label: 'Retain Activities', value: 'retainActivities' },
    { label: 'Retain Transit', value: 'retainTransit' },
    { label: 'Retain DMC', value: 'retainDmc' },
    { label: 'Retain Presentation Edits', value: 'retainPresentationEdits' },
  ];

  const isQuoteOnly = useQuoteOnly();
  const querySpecificAvailabilityMulti = useCalendarBySupplierWithRoomsMulti(segments, isQuoteOnly);

  const onClickPre = () => {
    if (!segmentsOriginal) {
      onClick();
    } else {
      // setValue on show so that by component<=>form has getValues of false.
      setValue('retainActivities', true);
      setValue('retainTransit', true);
      setValue('retainDmc', true);
      setValue('retainPresentationEdits', true);
      NiceModal.show(ConfirmDenyModal, {
        title: replaceBooking ? 'Booking Updates' : 'Updated quote confirmation',
        subtext: replaceBooking
          ? 'Changes will be made to your existing booking reference. Please note that some details, including activities, presentation edits, and custom transit, may be lost.'
          : 'You will receive a new quote number. Please note that some details, including activities, presentation edits, and custom transit, may be lost. Your original quote will be retained for reference to itinerary details.',
        fullWidth: true,
        children: replaceBooking ? null : (
          <Grid item xs={12}>
            <CheckboxGroup
              control={control}
              options={retainOptions}
              label="Would you like to retain details from your original quote? Please note that some transit and activities may be lost depending on the updated accommodation locations and nights."
              labelBold={false}
            />
            <hr className={s.hr} />
          </Grid>
        ),
        maxWidth: 'sm',
        showControls: true,
        showHide: true,
        disableHide: false,
        denyText: 'back',
        confirmText: 'confirm',
        onConfirm: modal => {
          onClick();
          modal.hide();
        },
      });
    }
  };

  const onClick = async () => {
    let accommodationRooms = {};
    const itinerary = {
      clientId: get(user, 'client.id'),
      name: 'Test Itinerary',
      itinerary: {
        adults,
        children,
        pax: adults + children,
        honeymoon,
        params: {
          rooms: defaultRooms,
          client: get(user, 'client'),
          customizations: {
            video: [{ isExcluded: true, supplierCode: OWN_ACCOMMODATION_SUPPLIER_CODE }],
          },
        },
        state: 'estimate',
      },
    };

    if (hasSegments) {
      let startDate = segments[0].startDate;
      let endDate = segments[0].endDate;
      let videoCustomizations = [];

      const itinerarySegments = segments.map((segment, idx) => {
        let segmentEndDate = addDays(parseDate(segment.endDateString), 1);

        let childrenAgesLeft = cloneDeep(childrenAges);
        if (isAfter(segmentEndDate, endDate)) {
          endDate = segmentEndDate;
        }
        if (isBefore(segment.startDate, startDate)) {
          startDate = segment.startDate;
        }
        let roomInfo = reduce(
          segment.rooms,
          (acc, room) => {
            let thisRoom = {
              roomType: '',
              adults: 0,
              children: [],
            };
            thisRoom.adults = room.adults;
            for (let numChildren = 0; numChildren < room.children; numChildren++) {
              thisRoom.children.push(childrenAgesLeft.shift());
            }
            switch (room?.room?.systemType) {
              case 'Twin':
                thisRoom.roomType = 'tr';
                break;
              case '_Double':
                thisRoom.roomType = 'dr';
                break;
              case 'Family':
                thisRoom.roomType = 'fr';
                break;
              case 'Single':
                thisRoom.roomType = 'sr';
                break;
              default:
                log.error('unknown room systemType: ', room);
                break;
            }
            if (thisRoom.adults === 1 && thisRoom.children.length === 0) {
              thisRoom.roomType = 'sr';
            }
            acc.rooms.push(thisRoom);
            acc.roomTypeIds.push(room.room.roomTypeId);
            acc.optionKeys.push(room.room.optionKey);
            acc.systemTypes.push(room.room.systemType);
            return acc;
          },
          { rooms: [], roomTypeIds: [], optionKeys: [], systemTypes: [] },
        );

        let staySegment = {
          type: 'stay',
          optionKeys: roomInfo.optionKeys,
          roomTypeIds: roomInfo.roomTypeIds,
          systemTypes: roomInfo.systemTypes,
          startDate: formatDate(segment.startDate),
          endDate: formatDate(segmentEndDate),
          nights: differenceInCalendarDays(segmentEndDate, segment.startDate),
          supplierCode: segment.supplierCode,
          supplierName: segment.campName, // Needed for copyToBuilder location logic.
          locationName: segment.regionName, // Needed for copyToBuilder location logic.
          countryCode: segment.countryRegionCode?.split('-')[0] ?? segment.countryCode, // Needed for isOnlyRestOfWorld logic.
        };
        if (segment.supplierCode === OWN_ACCOMMODATION_SUPPLIER_CODE) {
          videoCustomizations[idx] = { isExcluded: true, supplierCode: '' };
          delete staySegment.supplierCode;
          delete staySegment.locationName; // OAStay in e2e has this set, but should not.
          staySegment.arrangedBy = segment.arrangedBy === 'DMC' ? 'DMC' : 'Agent';
          staySegment.locationCode = segment.regionCode || 'ZZZ';
          staySegment.description = segment.title || 'Own Arranged Accommodation';
        }
        if (segment.dmcArrangedOnly) {
          staySegment.arrangedBy = 'DMC';
        }
        if (roomInfo.rooms.length === 0) {
          set(accommodationRooms, segment.supplierCode, defaultRooms);
        } else {
          set(accommodationRooms, segment.supplierCode, roomInfo.rooms);
        }

        return staySegment;
      }, []);
      if (videoCustomizations.length > 0) {
        videoCustomizations = videoCustomizations.map(el => el || {});
        itinerary.itinerary.params.customizations.video = videoCustomizations;
      }
      itinerary.itinerary.startDate = itinerary.startDate = formatDate(startDate);
      itinerary.itinerary.endDate = itinerary.endDate = formatDate(endDate);

      const segmentsDetailsPreQuote = retainDetailsPreQuote({
        retainDmc: getValues('retainDmc'),
        retainTransit: getValues('retainTransit'),
        retainActivities: getValues('retainActivities'),
        itinerarySegments,
        segmentsOriginal,
        itineraryOriginal,
      });

      let mergedSegments = segmentsDetailsPreQuote ? [...segmentsDetailsPreQuote] : [...itinerarySegments];

      // copyToBuilder.test.fixture
      //console.log({ mergedSegments, itinerarySegments, segmentsOriginal, itineraryOriginal }); // eslint-disable-line no-console
      //return;

      itinerary.itinerary.segments = mergedSegments;
      itinerary.itinerary.params.accommodationRooms = accommodationRooms;
      itinerary.name = `${uniq(segments.map(segment => segment.country)).join(', ')} Adventure`;

      log.debug('itinerary: ', itinerary);
    }

    itinerary.rates = rates;

    itinerary.origin = ITINERARY_ORIGIN.IB;

    if (copiedFrom) {
      itinerary.copiedFrom = copiedFrom;
      itinerary.isDuplicate = true; // WindowAPI.Duplicate logic for CopyToBuilder.
    }

    if (itineraryOriginal) {
      itinerary.rates = itineraryOriginal.rates;
      itinerary.baseRates = itineraryOriginal.baseRates; // Provide or rates will override.
      if (itineraryStateConfirmed(itineraryOriginal.itinerary.state)) {
        if (itineraryOriginal.finance.currency !== 'USD') {
          setItineraryFlag(itinerary, ITINERARY_FLAG.EXTRA_FOREIGN_SELL_MARKUP, true);
          itinerary.finance = { currency: itineraryOriginal.finance.currency };
        }
        itinerary.spotRates = itineraryOriginal.spotRates;
        itinerary.exchange = itineraryOriginal.exchange; // Needs spotRates.
      }
    }

    if (replaceBooking) {
      itineraryUpdateAndRequote.mutate({
        itineraryId: itineraryOriginal.id,
        itinerary: {
          ...itineraryOriginal.itinerary,
          params: itinerary.itinerary.params,
          startDate: itinerary.itinerary.startDate,
          segments: segmentsDateFloat(
            itinerary.itinerary.startDate,
            itineraryOriginal.itinerary.startDate,
            itinerary.itinerary.segments,
            false /* savedItinerary */,
          ),
        },
      });
    } else {
      getQuoteMutation.mutate(
        { itinerary },
        {
          onSuccess: (data, _variables, _context) => {
            const retainPresentationEdits = getValues('retainPresentationEdits');
            if (!replaceBooking && retainPresentationEdits) {
              Api.copyItineraryPresentationEdits(itineraryOriginal.id, data.id);
            }
          },
        },
      );
    }
  };

  const tripAdults = useBuilderStore(state => state.data.adults);
  const tripChildren = useBuilderStore(state => state.data.children);
  const dateMap = useBuilderStore(state => state.ui.dateMap);

  const errors = useMemo(() => {
    const errors = [];

    if (querySpecificAvailabilityMulti.length) {
      const samIsFetching = some(querySpecificAvailabilityMulti, 'isFetching');
      const samRequested =
        !samIsFetching &&
        querySpecificAvailabilityMulti.every(query => {
          return values(query?.data).every(date =>
            [CALENDAR_STATE.AVAILABLE_ON_REQUEST, CALENDAR_STATE.AVAILABLE, CALENDAR_STATE.AVAILABLE_ON_REQUEST_CHECK].includes(date.s),
          );
        });
      if (samIsFetching) errors.push('Fetching specific availability');
      if (!samRequested) errors.push('Unfortunately, the rooms highlighted in your selection are not available');
    }

    if (tripAdults + tripChildren === 0) {
      errors.push('No travelers have been assigned to the itinerary');
    }

    if (tripAdults === 0 && tripChildren > 0) {
      errors.push('An adult must be present if you have children');
    }

    const ownAccommodationSegments = filter(segments, segment => segment.supplierCode === OWN_ACCOMMODATION_SUPPLIER_CODE);
    if (some(ownAccommodationSegments, segment => !segment.regionCode)) {
      errors.push('Not all own arranged accommodations have a region selected');
    }
    const nonOwnAccommodationSegments = filter(segments, segment => segment.supplierCode !== OWN_ACCOMMODATION_SUPPLIER_CODE);
    if (
      some(nonOwnAccommodationSegments, segment => {
        if (segment.dmcArrangedOnly === true || segment.consultantInteractionRequired === true) {
          return;
        }
        const { adults, children } = reduce(
          segment?.rooms,
          (totals, room) => {
            totals.adults += room.adults || 0;
            totals.children += room.children || 0;
            return totals;
          },
          {
            adults: 0,
            children: 0,
          },
        );
        return adults !== tripAdults || children !== tripChildren;
      })
    ) {
      errors.push('Not all travelers have been assigned to rooms');
    }

    if (
      some(nonOwnAccommodationSegments, segment => {
        if (segment.minChildAge && segment.minChildAge > 0 && minTripChildAge && minTripChildAge < segment.minChildAge) {
          return true;
        }
      })
    ) {
      errors.push(`Not all accommodations can accept children aged ${minTripChildAge} years`);
    }

    const start = head(segments);
    const end = last(segments);

    if (start?.startDate && end?.endDate) {
      const hasGap = eachDayOfInterval({
        start: start.startDate,
        end: end.endDate,
      }).some(date => {
        const dateString = formatDate(date);
        return !(dateString in dateMap);
      });

      if (hasGap) {
        errors.push('There is a gap in the itinerary dates');
      }
    }

    if (!client) {
      errors.push('No client is selected');
    }

    return errors;
  }, [segments, dateMap, tripAdults, tripChildren, minTripChildAge, client, querySpecificAvailabilityMulti]);

  const disabled = size(errors) > 0;

  return (
    hasSegments && (
      <Tooltip
        title={
          disabled ? (
            <>
              {errors.map((error, index) => (
                <div className={classes.error} key={index}>
                  <ErrorIcon /> {error}
                </div>
              ))}
            </>
          ) : (
            ''
          )
        }
        placement="top"
      >
        <div className={clsx(classes.wrapper, disabled && classes.disabled)}>
          <Button
            disabled={disabled}
            className={classes.btn}
            color="primary"
            variant="contained"
            disableElevation
            startIcon={loading ? <CircularProgress className={classes.loader} size={14} /> : <PlayCircleFilledIcon />}
            onClick={() => (loading ? noop : onClickPre())}
            name="generate quote"
          >
            {replaceBooking ? 'Update Booking' : 'Generate Quote'}
          </Button>
        </div>
      </Tooltip>
    )
  );
};

export default GetQuoteButton;
